import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { widgetMenuOptionsAtom, widgetMenuOptionsByNameAtom } from "@/interface/AppStore";
import type { widgetOption } from "@/widgetMenu/widgetMenu";
import { applyValue, setNewOptions } from "../../lib";
import type { ActiveSubMenuState, DashType, LineOption, LineType } from "../../types";
import { SubMenu } from "../SubMenu/SubMenu";
import { LineTypeMenuContent } from "./LineTypeMenuContent";

interface LineTypeMenuProps {
    activeSubMenu: ActiveSubMenuState;
}

export function LineTypeMenu({ activeSubMenu }: LineTypeMenuProps) {
    const options = useAtomValue(
        widgetMenuOptionsByNameAtom("lineType"),
    ) as widgetOption<LineOption>[];
    const setWidgetMenuOptions = useSetAtom(widgetMenuOptionsAtom);

    const uniqueValues = options?.length
        ? [...new Map(options.map((o) => [JSON.stringify(o.value), o.value])).values()]
        : [];

    const firstValue = uniqueValues[0];

    const [lineType, setLineType] = useState<LineType>(firstValue?.lineType || "straight");
    const [dashType, setDashType] = useState<DashType>(firstValue?.dashType || "solid");

    const handleLineTypeButtonClick = useCallback(
        (lineType: LineType) => {
            const setNewLineTypeOptions = setNewOptions({ name: "lineType", setWidgetMenuOptions });

            setLineType(lineType);
            applyValue({ options, value: { lineType }, setNewOptions: setNewLineTypeOptions });
        },
        [options, setWidgetMenuOptions],
    );

    const handleDashTypeButtonClick = useCallback(
        (dashType: DashType) => {
            const setNewLineTypeOptions = setNewOptions({ name: "lineType", setWidgetMenuOptions });

            setDashType(dashType);
            applyValue({ options, value: { dashType }, setNewOptions: setNewLineTypeOptions });
        },
        [options, setWidgetMenuOptions],
    );

    useEffect(() => {
        if (firstValue) {
            setLineType(firstValue.lineType);
            setDashType(firstValue.dashType);
        }
    }, [firstValue]);

    if (!options?.length || !firstValue) {
        return null;
    }

    return (
        <SubMenu activeSubMenu={activeSubMenu}>
            <LineTypeMenuContent
                options={options}
                lineWidth={uniqueValues[0].lineWidth}
                selectedLineType={lineType}
                selectedDashType={dashType}
                onLineTypeButtonClick={handleLineTypeButtonClick}
                onDashTypeButtonClick={handleDashTypeButtonClick}
            />
        </SubMenu>
    );
}
